import { useCustomPulldownOptions } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/CustomTransactionItems/PulldownType/useCustomPulldownOptions';
import {
  CustomTransactionItemValue,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { MAX_CUSTOM_TRANSACTION_ITEMS_COUNT } from '@/features/transaction_items/List/Table/TableAction/TableAction';
import { useCallback, useMemo } from 'react';

export const useAggregatedCustomPulldownItems = (
  transactionProperties: TransactionProperty[]
) => {
  const customPulldowns = useMemo(
    () =>
      transactionProperties
        .filter(
          ({ type, componentType }) =>
            type === 'custom' && componentType === 'pulldown'
        )
        .map(({ id, label }) => ({
          id,
          label,
        })),
    [transactionProperties]
  );

  // This is a workaround necessary because Infinite Queries do not have a `useQueries` equivalent.
  // It violates the rules of hooks by calling hooks within a loop, but that shouldn't cause problems
  // because the loop is a constant size. It's just a shorter version of calling `useCustomPulldownOptions` 15 times
  const customPulldownHooks = [
    ...new Array(MAX_CUSTOM_TRANSACTION_ITEMS_COUNT),
  ].map((_, index) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCustomPulldownOptions(customPulldowns[index]?.id);
  });

  const aggregatedCustomPulldownItems = useMemo(() => {
    return customPulldownHooks.map(({ options }, index) => {
      const id = customPulldowns[index]?.id;
      const label = customPulldowns[index]?.label;
      return {
        id,
        label,
        options,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...customPulldownHooks, customPulldowns]);

  const getCustomPulldownHooksById = useCallback(
    (id?: string) => {
      return id === undefined
        ? undefined
        : customPulldownHooks.find((x) => x.id === id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...customPulldownHooks]
  );

  const findCustomPulldownOptionById = useCallback(
    (propertyId: string, optionId?: CustomTransactionItemValue) => {
      if (optionId === undefined) {
        return undefined;
      }

      const hooks = getCustomPulldownHooksById(propertyId);
      const formattedId = String(optionId ?? '');
      return hooks?.findDisplayNameById(formattedId) ?? '';
    },
    [getCustomPulldownHooksById]
  );

  const findCustomPulldownOptionByDisplayName = useCallback(
    (propertyId: string, displayName?: CustomTransactionItemValue) => {
      if (displayName === undefined) {
        return undefined;
      }

      const hooks = getCustomPulldownHooksById(propertyId);
      const formattedId = String(displayName ?? '');
      return hooks?.findIdByDisplayName(formattedId) ?? '';
    },
    [getCustomPulldownHooksById]
  );

  return {
    aggregatedCustomPulldownItems,
    findCustomPulldownOptionById,
    findCustomPulldownOptionByDisplayName,
  };
};
